import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Heading,
  TextField,
  Toggle,
  Divider,
  Flex,
  Box,
  IconButton,
  Text,
} from '@vibe/core';
import { Add, Delete, CloseSmall } from '@vibe/icons';
import { fetchMondayUsers, type MondayUser } from '../../api/monday.api';
import {
  createHolidayCalendar,
  updateHolidayCalendar,
  createHoliday,
  updateHoliday,
  deleteHolidayFromCalendar,
  assignUserToCalendar,
  getCalendarAssignments,
  removeCalendarAssignment,
  getHolidayCalendar,
  type HolidayCalendar,
  type Holiday,
} from '../../api/holiday.api';
import UserPicker, { type PickedUser } from '../shared/UserPicker';

// ─── Props ────────────────────────────────────────────────────────────────────

interface HolidayCalendarEditorProps {
  /** Existing calendar to edit, or null for creating a new one */
  calendar: HolidayCalendar | null;
  onSave: (saved: HolidayCalendar) => void;
  onCancel: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const HolidayCalendarEditor: React.FC<HolidayCalendarEditorProps> = ({
  calendar,
  onSave,
  onCancel,
}) => {
  const isEditing = calendar !== null;

  const [name, setName] = useState(calendar?.name ?? '');
  const [description, setDescription] = useState(calendar?.description ?? '');
  const [isDefault, setIsDefault] = useState(calendar?.is_default ?? false);
  const [holidays, setHolidays] = useState<Holiday[]>(
    calendar?.holidays ?? [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── User assignment state ─────────────────────────────────────────────────
  // Follows the CapacitySchemeEditor pattern: chip list + single-select dropdown
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>(
    calendar?.assignedUserIds ?? [],
  );
  const [assignments, setAssignments] = useState<
    Array<{ id: string; monday_user_id: string }>
  >([]);
  const [allUsersCache, setAllUsersCache] = useState<MondayUser[]>([]);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);

  // Build a user lookup map for names/avatars
  const userLookup = useMemo(() => {
    const map = new Map<string, MondayUser>();
    for (const u of allUsersCache) {
      map.set(String(u.id), u);
    }
    return map;
  }, [allUsersCache]);

  // Fetch available monday.com users
  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      setIsFetchingUsers(true);
      try {
        const users = await fetchMondayUsers();
        if (!cancelled) setAllUsersCache(users);
      } catch (err) {
        console.error('[HolidayCalendarEditor] Failed to fetch users:', err);
      } finally {
        if (!cancelled) setIsFetchingUsers(false);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch existing assignments when editing
  useEffect(() => {
    if (!isEditing || !calendar) return;
    let cancelled = false;
    const loadAssignments = async () => {
      try {
        const data = await getCalendarAssignments(calendar.id);
        if (!cancelled) {
          setAssignments(data);
          setAssignedUserIds(data.map((a) => a.monday_user_id));
        }
      } catch (err) {
        console.error('[HolidayCalendarEditor] Failed to fetch assignments:', err);
      }
    };
    loadAssignments();
    return () => {
      cancelled = true;
    };
  }, [isEditing, calendar]);

  // User IDs already assigned (for UserPicker exclusion)
  const excludeUserIds = useMemo(
    () => assignedUserIds,
    [assignedUserIds],
  );

  const getUserName = (userId: string) =>
    userLookup.get(userId)?.name ?? `User ${userId}`;
  const getUserAvatar = (userId: string) =>
    userLookup.get(userId)?.photo_thumb_small ?? null;

  // Add a user assignment (when editing, immediately persist)
  const handleAddUserAssignment = useCallback(
    async (user: PickedUser | null) => {
      if (!user) return;
      const userId = user.id;

      if (isEditing && calendar) {
        try {
          const assignment = await assignUserToCalendar(calendar.id, userId);
          setAssignments((prev) => [...prev, assignment]);
          setAssignedUserIds((prev) => [...prev, userId]);
        } catch (err) {
          console.error('[HolidayCalendarEditor] Failed to assign user:', err);
          setError('Failed to assign user');
        }
      } else {
        // For new calendars, just track locally (will persist after create)
        setAssignedUserIds((prev) => [...prev, userId]);
      }
    },
    [isEditing, calendar],
  );

  // Remove a user assignment (when editing, immediately persist)
  const handleRemoveAssignment = useCallback(
    async (userId: string) => {
      if (isEditing && calendar) {
        const assignment = assignments.find(
          (a) => a.monday_user_id === userId,
        );
        if (assignment) {
          try {
            await removeCalendarAssignment(calendar.id, assignment.id);
            setAssignments((prev) =>
              prev.filter((a) => a.id !== assignment.id),
            );
            setAssignedUserIds((prev) => prev.filter((id) => id !== userId));
          } catch (err) {
            console.error(
              '[HolidayCalendarEditor] Failed to remove assignment:',
              err,
            );
            setError('Failed to remove assignment');
          }
        }
      } else {
        setAssignedUserIds((prev) => prev.filter((id) => id !== userId));
      }
    },
    [isEditing, calendar, assignments],
  );

  // Add a new empty holiday entry
  const handleAddHoliday = useCallback(() => {
    setHolidays((prev) => [
      ...prev,
      {
        name: '',
        date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
      },
    ]);
  }, []);

  // Remove a holiday by index
  const handleRemoveHoliday = useCallback((index: number) => {
    setHolidays((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Update a holiday field
  const handleHolidayChange = useCallback(
    (index: number, field: keyof Holiday, value: string | boolean) => {
      setHolidays((prev) =>
        prev.map((h, i) => (i === index ? { ...h, [field]: value } : h)),
      );
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      setError('Calendar name is required');
      return;
    }

    // Validate holidays have names and dates
    const invalidHoliday = holidays.find(
      (h) => !h.name.trim() || !h.date,
    );
    if (invalidHoliday) {
      setError('All holidays must have a name and date');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let calendarId: string;

      if (isEditing && calendar) {
        // Update calendar metadata
        await updateHolidayCalendar(calendar.id, {
          name: name.trim(),
          description: description.trim() || null,
          isDefault,
        });
        calendarId = calendar.id;

        // Sync holidays: delete removed ones, create new ones
        const existingIds = new Set(
          (calendar.holidays ?? []).map((h) => h.id).filter(Boolean),
        );
        const currentIds = new Set(
          holidays.map((h) => h.id).filter(Boolean),
        );

        // Delete holidays that were removed
        for (const oldH of calendar.holidays ?? []) {
          if (oldH.id && !currentIds.has(oldH.id)) {
            await deleteHolidayFromCalendar(calendarId, oldH.id);
          }
        }

        // Update existing holidays that were modified, create new ones
        const existingHolidayMap = new Map(
          (calendar.holidays ?? []).filter((h) => h.id).map((h) => [h.id, h]),
        );
        for (const h of holidays) {
          if (!h.id || !existingIds.has(h.id)) {
            // New holiday
            await createHoliday(calendarId, {
              name: h.name,
              date: h.date,
              type: h.type ?? 'fixed',
            });
          } else {
            // Existing holiday — check if it was modified
            const orig = existingHolidayMap.get(h.id);
            if (orig && (orig.name !== h.name || orig.date !== h.date || (orig.type ?? 'fixed') !== (h.type ?? 'fixed'))) {
              await updateHoliday(calendarId, h.id, {
                name: h.name,
                date: h.date,
                type: h.type ?? 'fixed',
              });
            }
          }
        }
      } else {
        // Create new calendar
        const created = await createHolidayCalendar({
          name: name.trim(),
          description: description.trim() || null,
          isDefault,
        });
        calendarId = created.id;

        // Create all holidays
        for (const h of holidays) {
          await createHoliday(calendarId, {
            name: h.name,
            date: h.date,
            type: h.type ?? 'fixed',
          });
        }

        // Create all user assignments
        for (const userId of assignedUserIds) {
          await assignUserToCalendar(calendarId, userId);
        }
      }

      // Fetch the final enriched calendar and return it
      const saved = await getHolidayCalendar(calendarId);
      onSave(saved);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save calendar';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  }, [
    name,
    description,
    isDefault,
    holidays,
    assignedUserIds,
    isEditing,
    calendar,
    onSave,
  ]);

  return (
    <div className="rp-holiday-calendar-editor">
      {/* Header */}
      <Heading type={Heading.types.H3}>
        {isEditing ? 'Edit Holiday Calendar' : 'New Holiday Calendar'}
      </Heading>

      <div className="rp-holiday-calendar-editor__body">
        {/* Name */}
        <Box className="rp-holiday-calendar-editor__field">
          <label className="rp-holiday-calendar-editor__label">
            Calendar Name
          </label>
          <TextField
            size={TextField.sizes.SMALL}
            value={name}
            onChange={(value: string) => setName(value)}
            placeholder="e.g. US Federal Holidays"
            className="rp-holiday-calendar-editor__input"
          />
        </Box>

        {/* Description */}
        <Box className="rp-holiday-calendar-editor__field">
          <label className="rp-holiday-calendar-editor__label">
            Description (optional)
          </label>
          <TextField
            size={TextField.sizes.SMALL}
            value={description}
            onChange={(value: string) => setDescription(value)}
            placeholder="e.g. Official US federal holidays"
            className="rp-holiday-calendar-editor__input"
          />
        </Box>

        <Divider />

        {/* Set as default */}
        <Box className="rp-holiday-calendar-editor__field">
          <Flex
            justify={Flex.justify.SPACE_BETWEEN}
            align={Flex.align.CENTER}
          >
            <div>
              <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
                Set as Default
              </Text>
              <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                New users will be assigned this calendar automatically
              </Text>
            </div>
            <Toggle
              isSelected={isDefault}
              onChange={(checked: boolean) => setIsDefault(checked)}
              size="small"
              ariaLabel="Set as default calendar"
            />
          </Flex>
        </Box>

        <Divider />

        {/* Holiday list */}
        <Box className="rp-holiday-calendar-editor__field">
          <Flex
            justify={Flex.justify.SPACE_BETWEEN}
            align={Flex.align.CENTER}
          >
            <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
              Holidays ({holidays.length})
            </Text>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              leftIcon={Add}
              onClick={handleAddHoliday}
            >
              Add Holiday
            </Button>
          </Flex>

          {holidays.length === 0 && (
            <Text
              type={Text.types.TEXT2}
              style={{ color: '#676879', marginTop: '8px' }}
            >
              No holidays added yet. Click "Add Holiday" to get started.
            </Text>
          )}

          <div className="rp-holiday-calendar-editor__holidays">
            {holidays.map((holiday, index) => (
              <div
                key={holiday.id ?? index}
                className="rp-holiday-calendar-editor__holiday-row"
              >
                <TextField
                  size={TextField.sizes.SMALL}
                  value={holiday.name}
                  onChange={(value: string) =>
                    handleHolidayChange(index, 'name', value)
                  }
                  placeholder="Holiday name"
                  className="rp-holiday-calendar-editor__holiday-name"
                />
                <TextField
                  size={TextField.sizes.SMALL}
                  type={TextField.types.DATE}
                  value={holiday.date}
                  onChange={(value: string) =>
                    handleHolidayChange(index, 'date', value)
                  }
                  className="rp-holiday-calendar-editor__holiday-date"
                />
                <IconButton
                  icon={Delete}
                  size={IconButton.sizes.SMALL}
                  onClick={() => handleRemoveHoliday(index)}
                  ariaLabel={`Remove ${holiday.name || 'holiday'}`}
                />
              </div>
            ))}
          </div>
        </Box>

        <Divider />

        {/* Assignment section — hidden when default (applies to everyone) */}
        {isDefault ? (
          <Box className="rp-holiday-calendar-editor__field">
            <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
              Assigned Users
            </Text>
            <Text
              type={Text.types.TEXT2}
              style={{ color: '#676879', marginTop: '4px' }}
            >
              Default calendar — applies to all users automatically.
              No individual assignment needed.
            </Text>
          </Box>
        ) : (
          <Box className="rp-holiday-calendar-editor__field">
            <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
              Assigned Users
            </Text>
            <Text
              type={Text.types.TEXT2}
              style={{ color: '#676879', marginBottom: '8px' }}
            >
              Users assigned to this holiday calendar
            </Text>

            {assignedUserIds.length > 0 && (
              <div className="rp-holiday-calendar-editor__assignment-list">
                {assignedUserIds.map((userId) => (
                  <div
                    key={userId}
                    className="rp-holiday-calendar-editor__assignment-chip"
                  >
                    <Avatar
                      size={Avatar.sizes.SMALL}
                      type={getUserAvatar(userId) ? "img" : "text"}
                      src={getUserAvatar(userId) || undefined}
                      text={getUserName(userId).charAt(0).toUpperCase()}
                      ariaLabel={getUserName(userId)}
                    />
                    <Text type={Text.types.TEXT2}>
                      {getUserName(userId)}
                    </Text>
                    <IconButton
                      icon={CloseSmall}
                      size={IconButton.sizes.XXS}
                      onClick={() => handleRemoveAssignment(userId)}
                      ariaLabel="Remove assignment"
                    />
                  </div>
                ))}
              </div>
            )}

            <UserPicker
              value={null}
              onChange={handleAddUserAssignment}
              placeholder="Search for a user to add…"
              excludeUserIds={excludeUserIds}
            />
          </Box>
        )}
      </div>

      {/* Footer */}
      <div className="rp-holiday-calendar-editor__footer">
        {error && (
          <Text style={{ color: '#e2445c', marginBottom: '8px' }}>
            {error}
          </Text>
        )}
        <Flex gap={Flex.gaps.SMALL} justify={Flex.justify.END}>
          <Button kind={Button.kinds.TERTIARY} onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isSaving}>
            {isEditing ? 'Save Changes' : 'Create Calendar'}
          </Button>
        </Flex>
      </div>
    </div>
  );
};

export default HolidayCalendarEditor;
